import { FC, useEffect, useState } from 'react';

import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Grid, RadioGroup, RadioTile, Stretch } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import type { ExistingOrNewCustomerStepRendering } from '@sitecore/types/ExistingOrNewCustomerStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextSme } from '../../context';

interface FormFields {
  isCustomer: string;
  hasNewLocation: string;
  hasOtherEnergySupplier: string;
}

export const ExistingOrNewCustomerStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const isCustomer = useFlowSelector(state => state.context.isCustomer);
  const hasNewLocation = useFlowSelector(state => state.context.hasNewLocation);
  const hasOtherEnergySupplier = useFlowSelector(state => state.context.hasOtherEnergySupplier);
  const [hasOtherLocationQuestion, setHasOtherLocationQuestion] = useState<boolean | undefined>(undefined);

  const { ExistingOrNewCustomerStep: { fields: textualData } = {} } = usePlaceholderContent<{
    ExistingOrNewCustomerStep: ExistingOrNewCustomerStepRendering;
  }>();

  const formSchema = yup.object({
    isCustomer: yup.string().required(textualData?.choice?.isBusinessCustomerRadio?.value.requiredMessage),
    ...(hasOtherLocationQuestion && {
      hasNewLocation: yup.string().required(textualData?.choice?.newLocationRegistrationRadio?.value.requiredMessage),
    }),

    ...(!hasOtherLocationQuestion && {
      hasOtherEnergySupplier: yup
        .string()
        .required(textualData?.choice?.receivesEnergyFromOtherSupplierRadio?.value.requiredMessage),
    }),
  });

  const {
    control,
    formState: { errors, isSubmitSuccessful },
    getValues,
    handleSubmit,
    watch,
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      isCustomer: isCustomer?.toString() ?? 'false',
      hasNewLocation: hasNewLocation?.toString(),
      hasOtherEnergySupplier: hasOtherEnergySupplier?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const watchIsCustomer = watch('isCustomer');

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      const valuesToSubmit = {
        isCustomer: formData.isCustomer === 'true',
        ...(watchIsCustomer === 'true' && { hasNewLocation: formData.hasNewLocation === 'true' }),
        ...(watchIsCustomer === 'false' && { hasOtherEnergySupplier: formData.hasOtherEnergySupplier === 'true' }),
        // We assume customer is moving to the new address since they are not a customer
        // and does not have another energy supplier on the address
        isMoving: formData.isCustomer === 'false' && formData.hasOtherEnergySupplier === 'false',
      };

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...valuesToSubmit,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    setHasOtherLocationQuestion(watchIsCustomer === 'true');
  }, [watchIsCustomer]);

  const renderRadioGroup = (name: keyof FormFields, label?: string, options?: { name: string; label: string }[]) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            value={value}
            onValueChange={onChange}
            label={label ?? ''}
            error={errors[name]?.message}>
            <Stretch>
              <Grid gridTemplateColumns="1fr 1fr" gap="3">
                {options?.map(option => (
                  <RadioTile key={option.label} value={option.name}>
                    {option.label}
                  </RadioTile>
                ))}
              </Grid>
            </Stretch>
          </RadioGroup>
        )}
      />
    );
  };

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value} </Header.Text>}
      </Header>

      {renderRadioGroup(
        'isCustomer',
        textualData?.choice?.isBusinessCustomerRadio?.value.label,
        textualData?.choice?.isBusinessCustomerRadio?.value?.options,
      )}

      {watchIsCustomer === 'true' &&
        renderRadioGroup(
          'hasNewLocation',
          textualData?.choice?.newLocationRegistrationRadio?.value.label,
          textualData?.choice?.newLocationRegistrationRadio?.value?.options,
        )}

      {watchIsCustomer === 'false' &&
        renderRadioGroup(
          'hasOtherEnergySupplier',
          textualData?.choice?.receivesEnergyFromOtherSupplierRadio?.value.label,
          textualData?.choice?.receivesEnergyFromOtherSupplierRadio?.value?.options,
        )}

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
